/**
 * The table's denomination, its minimum, and what you sit down with.
 *
 * ## Why the room has money in it at all
 *
 * It used to be denominated in "units" — every hand one unit, every EV a decimal. That
 * is what a solver reads and it is not what a table looks like, and the difference
 * turns out to matter for the thing this room is for. A player who is told a decision
 * costs 0.46 units has learned an abstraction. A player who is told it cost eleven
 * dollars and fifty cents, out of the four hundred sitting in front of them, has felt
 * it. Variance is the hardest thing to teach in blackjack and it is impossible to teach
 * without a stack that goes down.
 *
 * ## What this is NOT
 *
 * There is no purchase, no prize, no withdrawal and no ledger. `BUY_IN` is granted by
 * clicking a button, as many times as anybody likes, and the page says so in those
 * words. The dollars exist because arithmetic in dollars is the arithmetic a player
 * will actually be doing at a real table, not because anything here is worth anything.
 *
 * ## The numbers
 *
 * Twenty-five is a green chip everywhere in the world, and a $25 minimum is the most
 * common table you will sit at outside of a locals casino. Four hundred is sixteen
 * hands — deliberately not a comfortable roll. Sixteen hands at a 0.5% house edge is
 * pure variance, and a player who busts out of it has learned the correct lesson about
 * what a sixteen-hand bankroll is.
 */

/** One chip. Green, everywhere. */
export const CHIP_VALUE = 25;

/** The smallest bet the table accepts, and — this table being flat — the only one. */
export const TABLE_MINIMUM = 25;

/** What a rack costs you at the window. Sixteen hands. */
export const BUY_IN = 400;

/** A hand's opening stake, in dollars. */
export const BASE_BET = TABLE_MINIMUM;

/**
 * Can this stack cover doubling or splitting?
 *
 * A real table will not let you double when you cannot match the bet, and this one
 * should not either — it is the rule that makes a short stack feel short. `stack` is
 * what is left AFTER the opening bet has been pushed out, which is how a dealer sees
 * it: the chips still in front of you, not the ones already in the circle.
 */
export function canCover(stackAfterBet: number, extra: number): boolean {
  return stackAfterBet >= extra;
}

/** Whether there is enough left to be dealt in at all. */
export function canBeDealt(stack: number): boolean {
  return stack >= TABLE_MINIMUM;
}

/**
 * Dollars, written the way a table writes them.
 *
 * Whole dollars get no decimals — a stack reads `$400`, not `$400.00`. A natural pays
 * three to two, so halves are real and do get them.
 */
export function money(dollars: number): string {
  const neg = dollars < 0;
  const v = Math.abs(dollars);
  const body = Number.isInteger(v) ? String(v) : v.toFixed(2);
  return `${neg ? "−" : ""}$${body}`;
}

/** The same, but always signed — for a number that is a change rather than a total. */
export function signedMoney(dollars: number): string {
  if (dollars === 0) return "$0";
  return `${dollars > 0 ? "+" : "−"}$${
    Number.isInteger(Math.abs(dollars)) ? Math.abs(dollars) : Math.abs(dollars).toFixed(2)
  }`;
}

/** A bet-unit figure from the engine, in this table's money. */
export const inDollars = (units: number): number => units * CHIP_VALUE;
